import axios from "axios";
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const sgd = (n) => `S$${Number(n).toFixed(2)}`;

// Resolve stored image references at render time. Uploaded files are stored
// as relative paths like "/api/files/onelook/uploads/xxx.jpg" so they follow
// whatever domain the app is deployed to. External URLs (Unsplash, CDN) pass
// through untouched.
export const resolveImage = (u) => {
  if (!u) return u;
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  if (u.startsWith("/")) return `${BACKEND_URL}${u}`;
  return u;
};

// Normalise a username (strip leading @, trim) and build social URLs.
// Returns {instagram, tiktok, xiaohongshu} where each value is either a full
// URL string or null if the input is empty.
export const socialLinks = (settings = {}) => {
  const strip = (v) => (v || "").trim().replace(/^@/, "");
  const ig = strip(settings.instagram_username);
  const tt = strip(settings.tiktok_username);
  let xhs = (settings.xiaohongshu_url || "").trim();
  if (xhs && !/^https?:\/\//i.test(xhs)) xhs = `https://${xhs}`;
  return {
    instagramUsername: ig,
    tiktokUsername: tt,
    xiaohongshuUrl: xhs || null,
    instagram: ig ? `https://instagram.com/${ig}` : null,
    tiktok: tt ? `https://tiktok.com/@${tt}` : null,
    xiaohongshu: xhs || null,
  };
};

export const api = axios.create({ baseURL: API });

// --- Pre-order helpers (Singapore time) ---
export const formatSG = (dateStr) => {
  if (!dateStr) return "";
  // dateStr is "YYYY-MM-DD" (date-only) — treat as a plain date for display
  const [y, m, d] = String(dateStr).split("-").map(n => parseInt(n, 10));
  if (!y || !m || !d) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Singapore" }).format(dt);
};

const addDaysISO = (dateStr, days) => {
  if (!dateStr) return "";
  const [y, m, d] = String(dateStr).split("-").map(n => parseInt(n, 10));
  if (!y || !m || !d) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};

// Current date in Singapore time as "YYYY-MM-DD"
const sgTodayISO = () => {
  const parts = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "Asia/Singapore" }).formatToParts(new Date());
  const o = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${o.year}-${o.month}-${o.day}`;
};

// settings = raw /api/settings dict. Returns a stable preorder state.
export const preorderState = (settings) => {
  const enabled = String(settings?.preorder_enabled || "").toLowerCase() === "true";
  const closeDate = settings?.preorder_close_date || "";
  const shipDate = closeDate ? addDaysISO(closeDate, 14) : "";
  const today = sgTodayISO();
  const closed = Boolean(enabled && closeDate && today > closeDate);
  return {
    enabled,
    closeDate,
    shipDate,
    closed,
    closeDateFmt: formatSG(closeDate),
    shipDateFmt: formatSG(shipDate),
    // active = preorder mode on AND not yet past close date
    active: enabled && Boolean(closeDate) && !closed,
  };
};
